/* eslint-disable no-local-storage/no-local-storage */
import { showNotification } from '@mantine/notifications';
import {
	fetchRetrievePrompt,
	fetchRetryPrompt,
	fetchTerminatePrompt,
	listPromptsQueryKey,
	retrievePromptQueryKey,
	type CreatePromptVariables,
	type EmbeddedPromptOut,
	type EmbeddedPromptParentOut,
} from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import type { QueryClient } from '@tanstack/react-query';
import { sortBy } from 'lodash-es';
import type { ObservableMap } from 'mobx';
import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import type { VirtuosoHandle } from 'react-virtuoso';
import type { AiAttachment, IUser, IWorkspace } from '../../api';
import { aiEmbeddedQueryKeyFactory } from '../../api/hooks/ai/constants';
import { type AIAssistantCurrentPage } from '../../components/AIAssistant/types';
import { generatePromptMessage } from '../../components/AIAssistant/utils';
import { getPromptContext, type PromptContextType } from './utils';

const POLLING_INTERVAL = 1000; // 1 second
const ACTIVE_STATUS = ['pending', 'running'];
const TITLE_POLLING_TIMEOUT = 10000; // 10 seconds
const AI_CHAT_DRAFT_PROMPT_KEY_PREFIX = 'ai-chat-draft-prompt';

// Helper functions for localStorage management
function getLocalStorageKey(id: string | undefined): string {
	return `${AI_CHAT_DRAFT_PROMPT_KEY_PREFIX}-${id ?? 'new'}`;
}

function getCachedInputPrompt(id: string | undefined): string {
	if (typeof window === 'undefined') {
		return '';
	}
	const key = getLocalStorageKey(id);
	const cached = localStorage.getItem(key);
	return cached ?? '';
}

function setCachedInputPrompt(id: string | undefined, value: string): void {
	if (typeof window === 'undefined') {
		return;
	}
	const key = getLocalStorageKey(id);
	if (value) {
		localStorage.setItem(key, value);
	} else {
		localStorage.removeItem(key);
	}
}

function removeCachedInputPrompt(id: string | undefined): void {
	if (typeof window === 'undefined') {
		return;
	}
	const key = getLocalStorageKey(id);
	localStorage.removeItem(key);
}

class AiChatPrompt {
	rootStore: AiChatStore;
	queryClient: QueryClient;
	prompt: EmbeddedPromptOut;
	pollingId: NodeJS.Timeout | null = null;
	shouldAutoOpenReferencedObjects: boolean = false;

	constructor(
		rootStore: AiChatStore,
		queryClient: QueryClient,
		prompt: EmbeddedPromptOut
	) {
		this.rootStore = rootStore;
		this.queryClient = queryClient;
		this.prompt = observable(prompt);

		makeAutoObservable(this);

		if (ACTIVE_STATUS.includes(prompt.status)) {
			this.shouldAutoOpenReferencedObjects = true;

			this.listen();
		}
	}

	get listening() {
		return this.pollingId !== null;
	}

	listen = () => {
		if (this.listening) {
			// already listening for changes
			return;
		}

		const refreshPrompt = async () => {
			// Check if polling was stopped while waiting for the API call
			if (!this.pollingId) {
				return;
			}

			const updatedPrompt = await this.queryClient.fetchQuery({
				queryKey: retrievePromptQueryKey({
					pathParams: { promptId: this.prompt.id },
				}),
				queryFn: () =>
					fetchRetrievePrompt({ pathParams: { promptId: this.prompt.id } }),
				staleTime: 0,
				gcTime: 0,
			});

			runInAction(() => {
				this.prompt.status = updatedPrompt.status;
				this.prompt.response = updatedPrompt.response;
				this.prompt.structured_response = updatedPrompt.structured_response;
				this.prompt.title = updatedPrompt.title;
				this.prompt.referenced_objects = updatedPrompt.referenced_objects;

				// Only continue polling if we haven't been stopped and status is still active
				if (this.pollingId && ACTIVE_STATUS.includes(updatedPrompt.status)) {
					this.pollingId = setTimeout(refreshPrompt, POLLING_INTERVAL);
					// Auto-scroll during streaming updates, but only if user is at the bottom.
					// This ensures users can scroll up to read earlier messages without being
					// forced back to the bottom on every streaming update.
					if (
						this.rootStore.shouldAutoScrollForMessage(this.prompt.id) &&
						updatedPrompt.response?.content
					) {
						// Double-check scroll position after setTimeout to ensure user hasn't
						// scrolled away in the meantime. This prevents race conditions where
						// the user scrolls up between the check and the scroll execution.
						setTimeout(() => {
							if (this.rootStore.shouldAutoScrollForMessage(this.prompt.id)) {
								this.rootStore.scrollToBottom('start');
							}
						}, 0);
					}
				} else {
					if (this.pollingId) {
						clearTimeout(this.pollingId);
						this.pollingId = null;
					}
					// Clear the auto-open flag after a delay to allow components to mount
					setTimeout(() => {
						this.shouldAutoOpenReferencedObjects = false;
					}, 10000); // 10 seconds delay
				}
			});
		};

		this.pollingId = setTimeout(refreshPrompt, POLLING_INTERVAL);
	};

	stopListening = action(async () => {
		if (this.pollingId) {
			clearTimeout(this.pollingId);
			this.pollingId = null;
		}
		this.prompt.status = 'stopped';
		try {
			await fetchTerminatePrompt({
				pathParams: { promptId: this.prompt.id },
			});
		} catch {
			// If terminate or fetch fails, set to stopped locally as fallback
			this.prompt.status = 'stopped';
		}
	});

	retry = action(async () => {
		const newPrompt = await fetchRetryPrompt({
			pathParams: { promptId: this.prompt.id },
		});
		this.prompt = newPrompt;
		if (ACTIVE_STATUS.includes(this.prompt.status)) {
			this.listen();
		}
	});

	updateUserFeedback = action((sentiment: string) => {
		this.prompt.user_feedback_raw = {
			sentiment,
		};
	});
}

type AiChatStoreProps = {
	queryClient: QueryClient;
	initialPrompt: EmbeddedPromptParentOut | null;
	createPrompt: (
		opts: CreatePromptVariables
	) => Promise<EmbeddedPromptParentOut>;
	currentUser: IUser;
	currentWorkspace: IWorkspace;
	isSidebar: boolean;
	onSubmit: (newPromptId: string) => void;
	onCreateQuestion: (
		message: EmbeddedPromptOut,
		parentPrompt: EmbeddedPromptParentOut,
		source: 'prompt' | 'response'
	) => void;
	onShowNegativeFeedbackModal: (
		messageId: string,
		onSubmit?: (sentiment: string) => void
	) => void;
	onShowPositiveFeedbackModal: (
		messageId: string,
		onSubmit?: (sentiment: string) => void
	) => void;
	startingPointEntityIdentifier?: string | null;
	initialId?: string;
};

export class AiChatStore {
	queryClient: QueryClient;
	initialPrompt: EmbeddedPromptParentOut | null;
	createPrompt: (
		opts: CreatePromptVariables
	) => Promise<EmbeddedPromptParentOut>;
	currentUser: IUser;
	currentWorkspace: IWorkspace;
	isSidebar: boolean;
	onSubmit: (newPromptId: string) => void;
	onCreateQuestion: (
		message: EmbeddedPromptOut,
		parentPrompt: EmbeddedPromptParentOut,
		source: 'prompt' | 'response'
	) => void;
	onShowNegativeFeedbackModal: (
		messageId: string,
		onSubmit?: (sentiment: string) => void
	) => void;
	onShowPositiveFeedbackModal: (
		messageId: string,
		onSubmit?: (sentiment: string) => void
	) => void;
	startingPointEntityIdentifier: string | null;
	initialId: string | undefined;

	messages: ObservableMap<string, AiChatPrompt> = observable.map();
	hasSubmittedPrompt = false;
	scrollAtBottom = true;
	scrollerHandle: VirtuosoHandle | null = null;
	inputPrompt = '';
	inputReRenderKey = 0;
	attachments: AiAttachment[] = [];
	webSearch = false;
	chart = false;
	isUploading = false;
	isStopping = false;
	activeTitlePolling: Map<string, ReturnType<typeof setTimeout>> = new Map();

	constructor({
		queryClient,
		initialPrompt,
		createPrompt,
		currentUser,
		currentWorkspace,
		isSidebar,
		onSubmit,
		onCreateQuestion,
		onShowNegativeFeedbackModal,
		onShowPositiveFeedbackModal,
		startingPointEntityIdentifier,
		initialId,
	}: AiChatStoreProps) {
		this.queryClient = queryClient;
		this.initialPrompt = initialPrompt;
		this.createPrompt = createPrompt;
		this.currentUser = currentUser;
		this.currentWorkspace = currentWorkspace;
		this.isSidebar = isSidebar;
		this.onSubmit = onSubmit;
		this.onCreateQuestion = onCreateQuestion;
		this.onShowNegativeFeedbackModal = onShowNegativeFeedbackModal;
		this.onShowPositiveFeedbackModal = onShowPositiveFeedbackModal;
		this.startingPointEntityIdentifier = startingPointEntityIdentifier ?? null;
		this.initialId = initialId;

		// Initialize inputPrompt from localStorage
		this.inputPrompt = getCachedInputPrompt(initialId);

		if (initialPrompt) {
			this.messages.set(
				initialPrompt.id,
				new AiChatPrompt(this, this.queryClient, initialPrompt)
			);

			sortBy(initialPrompt.children ?? [], 'created_at').forEach((child) => {
				this.messages.set(
					child.id,
					new AiChatPrompt(this, this.queryClient, child)
				);
			});

			// Initialize web search and chart toggles from the initial prompt
			this.webSearch = !!initialPrompt.web_search;
			this.chart = !!initialPrompt.chart;
		}

		makeAutoObservable(this, undefined, { autoBind: true });
	}

	setWebSearch = (webSearch: boolean) => {
		this.webSearch = webSearch;
	};

	setChart = (chart: boolean) => {
		this.chart = chart;
	};

	setIsUploading = (isUploading: boolean) => {
		this.isUploading = isUploading;
	};

	get messageIds() {
		return Array.from(this.messages.keys());
	}

	get isEmpty() {
		return this.messages.size === 0;
	}

	get currentOwner() {
		return this.getMessage(this.messageIds?.[0])?.prompt.user;
	}

	setScrollAtBottom = (scrollAtBottom: boolean) => {
		this.scrollAtBottom = scrollAtBottom;
	};

	setScrollerHandle = (handle: VirtuosoHandle | null) => {
		this.scrollerHandle = handle;
	};

	scrollToBottom(align: 'start' | 'end') {
		this.scrollerHandle?.scrollToIndex({
			index: this.messages.size - 1,
			align,
			behavior: 'smooth',
		});
	}

	/**
	 * Determines if we should auto-scroll when a message updates during streaming.
	 * Only auto-scrolls if:
	 * 1. The message is the last message in the chat
	 * 2. The message is currently streaming (status is 'pending' or 'running')
	 * 3. The user is already at the bottom of the chat (hasn't scrolled up)
	 *
	 * This prevents forcing users back to the bottom when they've scrolled up
	 * to read earlier messages during streaming.
	 */
	shouldAutoScrollForMessage(messageId: string) {
		const isLastMessage = this.isLastMessage(messageId);
		const isStreaming = this.getMessage(messageId)?.listening ?? false;
		return isLastMessage && isStreaming && this.scrollAtBottom;
	}

	setInputPrompt = action((inputPrompt?: string | null) => {
		this.inputPrompt = inputPrompt ?? '';
		// Save to localStorage whenever inputPrompt changes
		setCachedInputPrompt(this.initialId, this.inputPrompt);
	});

	// sets the input prompt and remounts the message, just setting the store doesn't work
	setInputPromptAndSubmit = async (
		inputPrompt?: string | null,
		submitOptions?: {
			personaId?: string;
			filter?: string;
			deepResearch?: boolean;
			webSearch?: boolean;
			chart?: boolean;
			currentPageInfo?: AIAssistantCurrentPage;
		}
	) => {
		if (!inputPrompt) {
			return;
		}
		this.setInputPrompt(inputPrompt);

		await this.submit({
			webSearch: submitOptions?.webSearch ?? this.webSearch,
			chart: submitOptions?.chart ?? this.chart,
			personaId: submitOptions?.personaId,
			filter: submitOptions?.filter,
			deepResearch: submitOptions?.deepResearch,
			currentPageInfo: submitOptions?.currentPageInfo,
		});
	};

	get currentUserIsOwner() {
		return this.currentOwner?.id === this.currentUser.id;
	}

	get showQuestionBanner() {
		if (this.messages.size === 0) {
			return false;
		}

		const hasUnansweredPrompts = Array.from(this.messages.values()).some(
			(message) => message.prompt.benchmark_raw?.fail_reason
		);

		return (
			hasUnansweredPrompts &&
			this.currentUserIsOwner &&
			this.currentUser.role === UserRole.VIEWER
		);
	}

	get showMessageLimitBanner() {
		return this.messages.size >= 10;
	}

	get isLoading() {
		return (
			this.isStopping ||
			Array.from(this.messages.values()).some((message) => message.listening)
		);
	}

	/**
	 * Gets the latest completed message ID for embedded_prompt context.
	 * This ensures we get suggestions based on the most recent completed message.
	 * Returns undefined if initialId is not set or there are no messages.
	 */
	get latestCompletedMessageId(): string | undefined {
		if (!this.initialId || this.messages.size === 0) {
			return undefined;
		}

		// Get messages in reverse order (most recent first)
		const messageIds = Array.from(this.messages.keys()).reverse();
		for (const messageId of messageIds) {
			const message = this.getMessage(messageId);
			if (message?.prompt.status === 'completed') {
				return messageId;
			}
		}

		// Fall back to initialId (parent prompt ID) if no completed messages found yet
		// This can happen when the parent prompt is still pending/running
		return this.initialId;
	}

	/**
	 * Computes the prompt context type and element ID for AI prompt suggestions.
	 * For embedded prompts, uses the latest completed message ID to provide
	 * context-aware suggestions based on the most recent conversation state.
	 */
	get promptContext(): {
		contextType: PromptContextType | null;
		elementId: string | undefined;
	} {
		// For embedded prompts, use the latest completed message ID if available
		if (this.initialId && this.latestCompletedMessageId) {
			return {
				contextType: 'embedded_prompt',
				elementId: this.latestCompletedMessageId,
			};
		}

		// Fall back to standard context detection
		return getPromptContext(this.initialId, this.startingPointEntityIdentifier);
	}

	/**
	 * Whether it's safe to prefetch suggestions (not loading and context is available).
	 */
	get canPrefetchSuggestions(): boolean {
		return !this.isLoading && !!this.promptContext.contextType;
	}

	async submit({
		filter,
		deepResearch,
		webSearch,
		chart,
		personaId,
		currentPageInfo,
	}: {
		filter?: string;
		deepResearch?: boolean;
		webSearch?: boolean;
		chart?: boolean;
		personaId?: string;
		currentPageInfo?: AIAssistantCurrentPage;
	}) {
		// filter and deepResearch are part of the API but not currently used
		void filter;
		void deepResearch;
		const prompt = this.inputPrompt;
		const attachments = [...this.attachments];

		this.inputPrompt = '';
		this.inputReRenderKey += 1;
		this.attachments = [];

		// Clear localStorage when prompt is submitted
		removeCachedInputPrompt(this.initialId);

		try {
			// Only await generatePromptMessage if there are attachments or currentPageInfo
			const promptMessage =
				attachments.length > 0 || currentPageInfo
					? await generatePromptMessage(
							prompt,
							currentPageInfo,
							attachments.map((attachment) => attachment.url)
						)
					: prompt;

			this.attachments = [];

			const newPrompt = await this.createPrompt({
				body: {
					prompt: promptMessage,
					parent: this.initialPrompt?.id,
					user_generated: true,
					persona_id: personaId,
					web_search: !!webSearch,
					chart,
					starting_point_entity_identifier: this.startingPointEntityIdentifier,
				},
			});

			this.queryClient.setQueryData(
				retrievePromptQueryKey({
					pathParams: { promptId: newPrompt.id },
				}),
				newPrompt
			);

			this.onSubmit?.(newPrompt.id);

			this.messages.set(
				newPrompt.id,
				new AiChatPrompt(this, this.queryClient, newPrompt)
			);

			this.hasSubmittedPrompt = true;

			// Poll for title updates if this is a root prompt (no parent)
			// This ensures the chat history list gets updated when async title generation completes
			const rootPromptId = !newPrompt.parent_id
				? newPrompt.id
				: this.initialPrompt?.id;
			if (rootPromptId) {
				this.pollForTitleUpdate(
					rootPromptId,
					newPrompt.title || this.initialPrompt?.title
				);
			}

			// timeout to ensure the new message is rendered before scrolling to the bottom
			setTimeout(() => {
				this.scrollToBottom('start');
			}, 0);
		} catch {
			this.inputPrompt = prompt;
			this.attachments = [...attachments];
			this.inputReRenderKey += 1;

			// Restore to localStorage if submission failed
			setCachedInputPrompt(this.initialId, this.inputPrompt);

			showNotification({
				title: 'Error',
				message: 'Failed to submit prompt',
				color: 'red',
			});
		}
	}

	retry = async (messageId: string) => {
		const message = this.messages.get(messageId);
		if (!message) {
			return;
		}

		message.retry();
	};

	stop = async () => {
		// Prevent multiple calls
		if (this.isStopping) {
			return;
		}

		this.isStopping = true;

		try {
			// Cancel all active title polling loops
			for (const timeoutId of this.activeTitlePolling.values()) {
				clearTimeout(timeoutId);
			}
			this.activeTitlePolling.clear();

			// Only stop listening messages that are actively polling
			await Promise.all(
				Array.from(this.messages.values())
					.filter((message) => message.listening)
					.map((message) => message.stopListening())
			);
		} catch (error) {
			this.isStopping = false;
			throw error;
		}

		this.isStopping = false;
	};

	getMessage(id: string) {
		return this.messages.get(id);
	}

	isLastMessage(id: string) {
		const { messageIds } = this;
		return messageIds.length > 0 && messageIds[messageIds.length - 1] === id;
	}

	shouldAutoOpenReferencedObjectsForMessage(messageId: string) {
		const message = this.getMessage(messageId);
		return message?.shouldAutoOpenReferencedObjects || false;
	}

	uploadAttachment = (attachment: AiAttachment) => {
		if (this.attachments.length >= 10) {
			showNotification({
				title: 'Upload limit reached',
				message: 'You can only upload up to 10 files at a time',
				color: 'red',
			});
			return;
		}
		this.attachments.push(attachment);
	};

	removeAttachment = (attachment: AiAttachment) => {
		const index = this.attachments.findIndex((a) => a.url === attachment.url);
		if (index !== -1) {
			this.attachments.splice(index, 1);
		}
	};

	showNegativeFeedbackModal = (messageId: string) => {
		const message = this.getMessage(messageId);
		this.onShowNegativeFeedbackModal(messageId, (sentiment) => {
			message?.updateUserFeedback(sentiment);
		});
	};

	showPositiveFeedbackModal = (messageId: string) => {
		const message = this.getMessage(messageId);
		this.onShowPositiveFeedbackModal(messageId, (sentiment) => {
			message?.updateUserFeedback(sentiment);
		});
	};

	pollForTitleUpdate = (
		promptId: string,
		initialTitle: string | null | undefined
	) => {
		// Cancel any existing polling loop for this promptId
		const existingTimeoutId = this.activeTitlePolling.get(promptId);
		if (existingTimeoutId !== undefined) {
			clearTimeout(existingTimeoutId);
			this.activeTitlePolling.delete(promptId);
		}

		// Use a ref-like approach to track polling state without causing re-renders
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let isPolling = true;
		let lastTitle = initialTitle;
		const startTime = Date.now();

		const cleanup = () => {
			isPolling = false;
			if (timeoutId !== null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
			this.activeTitlePolling.delete(promptId);
		};

		const pollTitle = async () => {
			if (!isPolling) {
				return;
			}

			const elapsed = Date.now() - startTime;
			if (elapsed > TITLE_POLLING_TIMEOUT) {
				cleanup();
				return;
			}

			try {
				const updatedPrompt = await this.queryClient.fetchQuery({
					queryKey: retrievePromptQueryKey({
						pathParams: { promptId },
					}),
					queryFn: () => fetchRetrievePrompt({ pathParams: { promptId } }),
					staleTime: 0,
					gcTime: 0,
				});

				// Only invalidate queries if title actually changed and is not empty
				if (
					updatedPrompt.title &&
					updatedPrompt.title !== lastTitle &&
					updatedPrompt.title !== initialTitle
				) {
					this.queryClient.invalidateQueries({
						queryKey: aiEmbeddedQueryKeyFactory.allLists(),
					});

					this.queryClient.invalidateQueries({
						queryKey: listPromptsQueryKey({}),
					});

					cleanup();
					return;
				}

				lastTitle = updatedPrompt.title;

				// Continue polling only if still active
				if (isPolling) {
					timeoutId = setTimeout(pollTitle, POLLING_INTERVAL);
					this.activeTitlePolling.set(promptId, timeoutId);
				}
			} catch {
				// Silently stop polling on error
				cleanup();
			}
		};

		// Start polling after initial delay
		timeoutId = setTimeout(pollTitle, POLLING_INTERVAL);
		this.activeTitlePolling.set(promptId, timeoutId);
	};
}
